/**
 * 统一登录态（自建后端）
 *
 * 迁移期策略：
 * 1. 优先走自建后端 api.jacvipac.cc 登录
 * 2. 后端里还没有这个账号（MySQL 用户表空着）时，回退到 Supabase 校验密码，
 *    校验通过后自动在 MySQL 里建一个同密码的镜像账号，用户无感
 * 3. 登录成功后同时尝试建立 Supabase 会话，保证尚未迁移的数据页面还能正常读
 *    （第二批数据迁移完成后，这一步会自动去掉）
 */
import {
  authApi,
  ApiError,
  saveSession,
  clearSession,
  getToken,
  getCachedUser,
  subscribeAuth,
  type ApiUser,
} from '@/lib/api';
import { supabase } from '@/lib/supabase';

const toAuthEmail = (phone: string) => `${phone}@jinan-gold.com`;

export type { ApiUser };

/** 用户名/密码错误（前端可用于区分文案） */
export function isCredentialError(err: unknown): boolean {
  const status = (err as ApiError)?.status ?? 0;
  return status === 401;
}

export function isAccountExistsError(err: unknown): boolean {
  const status = (err as ApiError)?.status ?? 0;
  const msg = String((err as Error)?.message || '');
  return status === 409 || msg.includes('已注册') || msg.includes('已存在');
}

// 迁移期：让 Supabase 会话与自建后端会话并存
async function bridgeSupabaseSession(phone: string, password: string): Promise<boolean> {
  try {
    const { data, error } = await supabase.auth.signInWithPassword({
      email: toAuthEmail(phone),
      password,
    });
    return !error && !!data.session;
  } catch {
    return false;
  }
}

// 老账号自动镜像到 MySQL（已存在则忽略）
async function importLegacyAccount(phone: string, password: string): Promise<void> {
  try {
    await authApi.register(phone, password);
  } catch (err) {
    if (!isAccountExistsError(err)) {
      // 迁移失败不阻断登录，下一次登录会再试
      console.warn('[session] 老账号镜像失败:', (err as Error)?.message);
    }
  }
}

/**
 * 登录：手机号 + 密码
 * 抛出的错误带有后端原始中文文案，可直接展示
 */
export async function login(phone: string, password: string): Promise<ApiUser> {
  const normalized = phone.trim();

  try {
    const result = await authApi.login(normalized, password);
    saveSession(result.token, result.user);
    void bridgeSupabaseSession(normalized, password);
    return result.user;
  } catch (err) {
    const status = (err as ApiError)?.status ?? 0;
    const shouldTryLegacy = status === 401 || status === 404 || status === 400;
    if (!shouldTryLegacy) throw err;

    // MySQL 里没有这个账号 → 用 Supabase 校验一次老密码
    const legacyOk = await bridgeSupabaseSession(normalized, password);
    if (!legacyOk) throw err;

    await importLegacyAccount(normalized, password);
    const result = await authApi.login(normalized, password);
    saveSession(result.token, result.user);
    return result.user;
  }
}

/**
 * 注册：自建后端建档后立即进入登录态
 */
export async function register(phone: string, password: string, name?: string): Promise<ApiUser> {
  const result = await authApi.register(phone.trim(), password, name);
  saveSession(result.token, result.user);
  return result.user;
}

/** 退出登录，同时清掉两边的登录态 */
export async function logout(): Promise<void> {
  clearSession();
  try {
    await supabase.auth.signOut();
  } catch {
    // 忽略
  }
  try {
    localStorage.removeItem('jinan_user_login_at');
  } catch {
    // ignore
  }
}

/** 本地是否有登录态（同步） */
export function hasSession(): boolean {
  return !!getToken();
}

/** 当前用户（优先本地缓存，必要时向后端确认） */
export async function currentUser(): Promise<ApiUser | null> {
  const token = getToken();
  if (!token) return null;

  try {
    const { user } = await authApi.me();
    saveSession(token, user);
    return user;
  } catch (err) {
    const status = (err as ApiError)?.status ?? 0;
    if (status === 401) {
      clearSession();
      return null;
    }
    // 网络异常时退回本地缓存，避免错误地把用户踢下线
    return getCachedUser();
  }
}

/** 订阅登录态变化 */
export function onAuthChange(listener: (user: ApiUser | null) => void): () => void {
  return subscribeAuth(listener);
}